<template>
  <div
    id="action-recommendation"
    class="relative flex justify-center w-full py-6 lg:px-6 bg-slate-200"
  >
    <div class="w-full border bg-pure-white rounded-3xl border-slate-400">
      <div
        id="recommendation-header"
        class="flex items-center justify-between p-6 lg:p-10"
      >
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <div
              class="hidden p-2 bg-orange-300 lg:block"
              :style="{ borderRadius: '4px' }"
            >
              <BlIcon icon="tooltip" class="w-6 h-6" />
            </div>
            <BlTextTitle4 text="Action recommendations" />
            <BlBubbleCount :count="totalActionRecommendations || 0" />
          </div>
          <BlTextBody2
            text="Below are the targets that have successfully reached their success metrics and may need further action."
          />
        </div>
        <div
          v-if="selectedRecommendation.length"
          class="flex items-center gap-2"
        >
          <BlInputButton
            type="outlined"
            :label="`Ignore (${selectedRecommendation.length})`"
            :loading="submitLoading"
            @clicked="onIgnore(selectedRecommendation)"
          />
          <BlInputButton
            :label="`Update (${selectedRecommendation.length})`"
            :loading="submitLoading"
            @clicked="onAccept(selectedRecommendation)"
          />
        </div>
      </div>
      <div id="recommendation-metric" class="px-10 py-5">
        <div v-if="pageLoading" class="grid place-items-center h-92">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!actionRecommendations.length"
          class="grid place-items-center h-92"
        >
          <BlTextBody1
            text="Currently, there are no targets that have yet passed the success metric."
            class="text-center lg:w-128 text-grey-400"
          />
        </div>
        <div v-else class="relative pb-4 overflow-x-auto scrollbar-lg">
          <div id="metric-header" class="flex text-grey">
            <div
              class="sticky left-0 z-10 flex items-start flex-shrink-0 gap-2 px-3 py-2 border-r rounded-l bg-slate-200 w-72 border-slate-400"
            >
              <BlInputCheck
                :checked="masterChecked"
                :indeterminate="masterIndeterminate"
                @changed="onChangeSelectedAll"
                class="mt-1"
              />
              <BlTextSubtitle2 text="Target" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-slate-200">
              <BlTextSubtitle2 text="Curriculum" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-slate-200">
              <BlTextSubtitle2 text="Method" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-slate-200">
              <BlTextSubtitle2 text="Latest session by" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-slate-200">
              <BlTextSubtitle2 text="Success metric" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-slate-200">
              <BlTextSubtitle2 text="Total successful sessions" />
            </div>
            <div class="flex-shrink-0 w-48 px-3 py-2 bg-slate-200">
              <BlTextSubtitle2 text="Number of consecutive success" />
            </div>
            <div class="flex-shrink-0 px-3 py-2 rounded-r bg-slate-200 w-104">
              <BlTextSubtitle2 text="Recommended action" />
            </div>
          </div>
          <div
            v-for="recommendation in actionRecommendations"
            :key="recommendation.id"
            class="flex h-18"
          >
            <div
              class="sticky left-0 z-10 flex items-center flex-shrink-0 gap-2 px-3 py-4 border-b border-r w-72 border-slate-400 bg-pure-white"
              :title="recommendation.target.name"
            >
              <BlInputCheck
                :checked="selectedRecommendation.includes(recommendation.id)"
                @changed="onChangeSelected(recommendation.id)"
              />
              <BlTextBody1
                :text="recommendation.target.name"
                class="truncate text-grey"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 gap-2 px-3 py-4 border-b border-slate-400"
              :title="recommendation.target.curriculum_name"
            >
              <div
                class="flex-shrink-0 w-4 h-4 rounded-full"
                :style="{ background: recommendation.target.curriculum_color }"
              />
              <BlTextBody1
                :text="recommendation.target.curriculum_name"
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-slate-400"
              :title="recommendation.target.type_name"
            >
              <BlTextBody1
                :text="recommendation.target.type_name"
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-slate-400"
            >
              <BlTextBody1
                v-if="!recommendation.latest_session_by"
                title="Deleted user"
                text="Deleted user"
                class="italic truncate text-grey-400"
              />
              <router-link
                v-else
                :event="currentUser.role.includes('admin') ? 'click' : ''"
                target="_blank"
                :to="{
                  name: 'TeamSessionHistory',
                  params: {
                    user_id: recommendation.latest_session_by.id,
                    redirect_target: `/clients/${id}`,
                    redirect_label: 'Client recommendations'
                  }
                }"
              >
                <div
                  :title="recommendation.latest_session_by.name"
                  class="flex items-center justify-center h-8 px-3 truncate border rounded bg-success-100 border-success-100 max-w-40"
                  :class="[
                    currentUser.role.includes('admin')
                      ? 'hover:border-success-400'
                      : 'cursor-default'
                  ]"
                >
                  <BlTextBody1
                    class="truncate text-success"
                    :text="recommendation.latest_session_by.name"
                  />
                </div>
              </router-link>
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-slate-400"
              :title="generateSuccessMetric(recommendation.target)"
            >
              <BlTextBody1
                :text="generateSuccessMetric(recommendation.target)"
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-slate-400"
            >
              <BlTextBody1
                :text="
                  recommendation.target.total_success
                    ? `${recommendation.target.total_success} session(s)`
                    : '-'
                "
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center flex-shrink-0 w-48 px-3 py-4 border-b border-slate-400"
            >
              <BlTextBody1
                :text="
                  recommendation.target.consecutive_success
                    ? `${recommendation.target.consecutive_success} time(s)`
                    : '-'
                "
                class="truncate text-grey-400"
              />
            </div>
            <div
              class="flex items-center justify-between flex-shrink-0 gap-4 px-3 py-4 border-b w-104 border-slate-400"
            >
              <div class="flex items-center gap-2">
                <StatusChip :status="recommendation.target.status" />
                <span class="text-grey-300">→</span>
                <StatusChip :status="recommendation.recommended_action" />
              </div>
              <div
                v-if="!selectedRecommendation.length"
                class="flex items-center gap-2"
              >
                <BlInputButton
                  type="outlined"
                  label="Ignore"
                  :loading="submitLoading"
                  @clicked="onIgnore([recommendation.id])"
                />
                <BlInputButton
                  label="Update"
                  :loading="submitLoading"
                  @clicked="onAccept([recommendation.id])"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <BlInputPagination
        v-if="totalActionRecommendations > 10"
        class="my-6"
        :count="
          Math.ceil(
            totalActionRecommendations / ($route.query['per_page'] || 10)
          )
        "
        :page="page"
        @changed="changePage"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import StatusChip from "./target/StatusChip.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlIcon from "../BlIcon.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextTitle4 from "../BlTextTitle4.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputCheck from "../BlInputCheck.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "ClientRecommendation",
  props: ["id"],
  components: {
    StatusChip,
    BlTextTitle4,
    BlTextBody2,
    BlBubbleCount,
    BlIcon,
    BlTextSubtitle2,
    BlTextBody1,
    BlInputButton,
    BlInputCheck,
    BlInputPagination
  },
  data() {
    return {
      pageLoading: true,
      page: 1,
      submitLoading: false,
      selectedRecommendation: [],
      targetTypes: {
        "Target::Percentage": "Percentage",
        "Target::Pir": "Partial interval recording",
        "Target::Duration": "Duration",
        "Target::Frequency": "Frequency",
        "Target::Prompting": "Prompting"
      }
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "actionRecommendations",
      "totalActionRecommendations"
    ]),
    masterChecked() {
      if (!this.actionRecommendations.length) return false;
      return this.actionRecommendations.every(a =>
        this.selectedRecommendation.includes(a.id)
      );
    },
    masterIndeterminate() {
      if (!this.actionRecommendations.length) return false;
      return (
        this.actionRecommendations.findIndex(a =>
          this.selectedRecommendation.includes(a.id)
        ) > -1 && !this.masterChecked
      );
    },
    getParams() {
      const searchParams = this.$route.query;
      let params = `?page=${this.page}&per_page=${searchParams["per_page"] ||
        10}`;
      return params;
    }
  },
  mounted() {
    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    this.fetchActionRecommendation();
  },
  methods: {
    ...mapActions([
      "getActionRecommendations",
      "acceptActionRecommendation",
      "ignoreActionRecommendation",
      "showToast"
    ]),
    generateSuccessMetric(target) {
      let prefix = "";
      let goalText = "";
      let suffix = "";

      // prefix
      if (target.success_metric === "equal to or greater than goal") {
        prefix = "≥ ";
      }
      if (target.success_metric === "less than goal") {
        prefix = "< ";
      }

      // goal text
      if (target.type_name === "Duration") {
        goalText = target.goal_time;
      } else if (
        target.type_name === "Prompting" &&
        target.prompting_format === "classic"
      ) {
        goalText = `≥ ${target.goal}`;
      } else {
        goalText = target.goal;
      }

      // suffix
      if (target.type_name === "Percentage") suffix = "%";
      if (target.type_name === "Partial interval recording") suffix = "%";
      if (
        target.type_name === "Prompting" &&
        target.prompting_format === "custom"
      ) {
        suffix = "%";
      }
      if (target.type_name === "Frequency") suffix = " attempt(s)";
      if (
        target.type_name === "Prompting" &&
        target.prompting_format === "classic"
      ) {
        suffix = ` attempt(s) ${target.success_metric} prompts`;
      }

      return prefix + goalText + suffix;
    },
    fetchActionRecommendation() {
      this.pageLoading = true;
      this.selectedRecommendation = [];
      this.getActionRecommendations({
        id: this.id,
        params: this.getParams
      }).then(() => {
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/clients/${this.id}/recommendation${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    changePage(page) {
      this.page = page;
      this.fetchActionRecommendation();
    },
    onChangeSelected(id) {
      if (this.selectedRecommendation.includes(id)) {
        this.selectedRecommendation.splice(
          this.selectedRecommendation.indexOf(id),
          1
        );
      } else this.selectedRecommendation.push(id);
    },
    onChangeSelectedAll() {
      if (
        this.selectedRecommendation.length === this.actionRecommendations.length
      ) {
        this.selectedRecommendation = [];
      } else if (this.actionRecommendations) {
        this.selectedRecommendation = this.actionRecommendations.map(a => a.id);
      }
    },
    onAccept(ids) {
      this.submitLoading = true;
      this.acceptActionRecommendation({
        ids: ids.join(",")
      }).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        const title = "Target changed to Mastered";
        const message = `Success! <b>${ids.length}</b> targets have been marked as "Mastered."`;
        this.showToast({ title, message, type: "success" });
        this.fetchActionRecommendation();
      });
    },
    onIgnore(ids) {
      this.submitLoading = true;
      this.ignoreActionRecommendation({
        ids: ids.join(",")
      }).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        const title = "Action recommendations ignored";
        const message = `You've chosen to ignore recommendations for <b>${ids.length} target(s).</b> The <br /> recommendations will reappear once the target(s) meet the <br /> success metric in the next session.`;
        this.showToast({ title, message, type: "success" });
        this.fetchActionRecommendation();
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/ClientRecommendation.vue